import { useEffect, useRef, useState } from "react";

//Importing the Subcomponents
import CardView from "../cardView/CardView";
import SideForm from "../sideForm/SideForm";
import styled from "styled-components";
import useScreenSize from "../../hooks/useScreenSize";
import DisplayView from "../displayView/DisplayView";

import { useStore } from "../pageView/Page";

//import resizable components
import { ResizeHandle, Panel, PanelGroup } from "../resizable/Resizable";

/*
 * `wideScreen` on the multi view — what a screen wider than the page gets:
 *
 *   (absent)  the page stops at `width`; the rest is empty
 *   'fill'    no stop: the main view takes the extra (a grid wants it)
 *   'panel'   the main view keeps `mainWidth` and the side panel takes the
 *             extra — a document reads better larger — up to its `maxSize`
 *
 * `align` — where a page that stops sits in what is left: 'left' (the
 * default) or 'center'. Nothing to align with 'fill', which never stops.
 */
const MultiViewWrapper = styled.div`
  display: flex;
  gap: 1rem;
  max-width: ${({ width, $wideScreen }) => $wideScreen === 'fill' ? 'none' : (width ? width : "")};
  width: 100%;
  margin: 0 0 -1rem -1rem;
  /* Centred only once there is room to spare: below that it keeps the pull
     to the left every multi view has. */
  ${({ width, $wideScreen, $align }) => width && $align === 'center' && $wideScreen !== 'fill' && `
    margin-left: max(-1rem, calc((100% - ${width}) / 2));
  `}

`

const Wrapper = styled.div`
  padding: 1rem 0.5rem 1rem 1rem;
`

// The folded panel: a fixed strip beside the group, not a panel inside it.
const FoldedStrip = styled.div`
  flex: 0 0 48px;
  align-self: stretch;
`

/*
 * A `collapsible` side panel folds down to a thin strip: its toggle on top
 * and whatever its sections count (a line's documents) below.
 *
 * Folded is this view's own state, not the resizing library's. Folded, the
 * panel leaves the group altogether — the main view takes the whole of it and
 * the strip stands beside it at a fixed width. The library's own collapse was
 * tried first and did not hold: it keeps "collapsed" as a share of the group,
 * worked out afresh whenever the group changes width (the window, the
 * navigation bar), and after one it no longer knew the panel was folded — the
 * toggle stopped answering, or the open panel showed squeezed into the strip.
 *
 * Dragged narrower than `minSize`, the panel folds when the pointer is let
 * go. It opens again at the width it last had open. Both — folded or open,
 * and that width — are remembered per panel, so the next record opens the way
 * the last one was left.
 */
const FOLD_DRAG_FLOOR = '120px'

const storageKey = (type, what) => `sidePanel.${type}.${what}`

const readStored = (type, what) => {
  try { return localStorage.getItem(storageKey(type, what)) } catch { return null }
}

const writeStored = (type, what, value) => {
  try { localStorage.setItem(storageKey(type, what), String(value)) } catch { /* storage unavailable */ }
}

const ResizableView = ({ options, validated, sidePanelConfig, config, dispatch, type }) => {

  //console.log('[] OPTIONS: ', options)
  //console.log('[] CONFIG: ', config)

  const [entity] = useStore((s) => s['entity'])
  const { isMobile } = useScreenSize()

  const isCard    = config.viewKind === 'card'    || config.mainFormType === 'cardView'
  const isDisplay = config.viewKind === 'display' || config.mainFormType === 'displayView'
  const mainView  = config.view ?? (isCard ? config.cardViewType : config.displayViewType)

  const collapsible = !!sidePanelConfig?.collapsible
  const panelKey = config.sidePanel ?? config.sidePanelType
  const foldBelow = parseInt(sidePanelConfig?.minSize ?? '280', 10)

  /*
   * Which panel's width is remembered as the user leaves it: normally the
   * side panel, which also keeps its width in pixels when the screen changes,
   * so the main view takes the difference; with `wideScreen: 'panel'` the
   * main view's, and a change of screen is shared between the two.
   */
  const panelTakesSpare = config.wideScreen === 'panel'
  // Only a collapsible panel or a page that says how it uses a wide screen
  // keeps and remembers widths; every other multi view sizes as it always has.
  const managed = collapsible || !!config.wideScreen

  const [collapsed, setCollapsed] = useState(() => collapsible && readStored(panelKey, 'collapsed') === 'true')
  // The kept panel's width as last left, in pixels; none yet means its default.
  // Kept per page, not per panel: the journal card and the Fast Journal share
  // their documents panel, but not the width of what sits beside it. Folded or
  // open stays per panel — documents put away are put away everywhere.
  const widthOwner = type ?? panelKey
  const keptWidthKey = panelTakesSpare ? 'mainWidth' : 'width'
  const keptWidthRef = useRef(managed ? Number(readStored(widthOwner, keptWidthKey)) || null : null)
  const mainPanelRef = useRef(null)
  const sidePanelRef = useRef(null)
  // The side panel's width right now, as the library reports it.
  const widthRef = useRef(null)
  // A fold only ever follows a drag of the handle — never the window or the
  // navigation bar narrowing the page.
  const draggingRef = useRef(false)

  const setFolded = (folded) => {
    setCollapsed(folded)
    writeStored(panelKey, 'collapsed', folded)
  }

  const keepWidth = (px) => {
    keptWidthRef.current = Math.round(px)
    writeStored(widthOwner, keptWidthKey, keptWidthRef.current)
  }

  const handleSideResize = (size) => {
    if (!size?.inPixels) return
    widthRef.current = size.inPixels
    if (managed && !panelTakesSpare && size.inPixels >= foldBelow) keepWidth(size.inPixels)
  }

  const handleMainResize = (size) => {
    // Remembered only from a drag: folded, the main view has the group to itself.
    if (size?.inPixels && draggingRef.current && widthRef.current >= foldBelow) keepWidth(size.inPixels)
  }

  // After a drag is let go: narrower than it opens to, it folds.
  const handleLayoutChanged = () => {
    const dragged = draggingRef.current
    draggingRef.current = false
    if (dragged && collapsible && !collapsed && widthRef.current && widthRef.current < foldBelow) setFolded(true)
  }

  const handlePointerDown = (e) => {
    if (e.target.closest?.('[data-separator]')) draggingRef.current = true
  }

  const toggleCollapsed = () => setFolded(!collapsed)

  const showPanel = sidePanelConfig && !(collapsible && collapsed)

  /*
   * A panel put back into the group takes whatever share the library hands
   * it, not its defaultSize — so on opening, the kept panel is set to its
   * width outright and the other takes the rest.
   *
   * Not straight away: the library registers a panel added to the group in
   * its own layout pass, and until then resize() throws ("Layout not found
   * for Panel") — thrown from an effect, that took the whole page down. So it
   * waits for the next frames, and a resize that still fails only leaves the
   * library's own split.
   */
  useEffect(() => {
    if (!managed || !showPanel) return
    const [ref, width] = panelTakesSpare
      ? [mainPanelRef, keptWidthRef.current ? `${keptWidthRef.current}px` : config.mainWidth]
      : [sidePanelRef, keptWidthRef.current ? `${keptWidthRef.current}px` : sidePanelConfig.defaultSize]
    if (!width) return
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        try {
          ref.current?.resize(panelTakesSpare ? mainWidthLeavingPanel(width) : width)
        } catch { /* not laid out yet: keep the library's split */ }
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [showPanel])

  /*
   * The main view's width as the panel opens: its own, unless that leaves
   * the panel less than its defaultSize — a wide grid on an ordinary screen
   * would otherwise open its documents a sliver. Then the main view gives way.
   */
  const mainWidthLeavingPanel = (width) => {
    const main = mainPanelRef.current?.getSize()?.inPixels
    const side = sidePanelRef.current?.getSize()?.inPixels
    const wanted = parseFloat(width)
    if (!main || side === undefined || !String(width).endsWith('px')) return width
    const total = main + side
    const share = String(sidePanelConfig.defaultSize ?? '30%')
    const panelAtLeast = share.endsWith('%') ? total * parseFloat(share) / 100 : parseFloat(share)
    return `${Math.round(Math.min(wanted, total - panelAtLeast))}px`
  }

  const sidePanel = showPanel && (
    <Panel
      id="side"
      // Taking the spare, it is whatever the main view leaves.
      defaultSize={panelTakesSpare ? undefined
        : keptWidthRef.current ? `${keptWidthRef.current}px` : sidePanelConfig.defaultSize}
      {...(collapsible && { minSize: FOLD_DRAG_FLOOR })}
      // The widest it can be dragged — and, taking a wide screen's spare,
      // the widest it grows.
      {...(sidePanelConfig.maxSize && { maxSize: sidePanelConfig.maxSize })}
      {...(managed && !panelTakesSpare && { groupResizeBehavior: 'preserve-pixel-size' })}
      panelRef={sidePanelRef}
      onResize={handleSideResize}
    >
      <SideForm formType={config.viewKind ?? config.mainFormType}
        config={sidePanelConfig} validated={validated} options={options}
        {...(collapsible && { onToggleCollapsed: toggleCollapsed })} ></SideForm>
    </Panel>
  )

  /*
   * Taking the spare, the page is never wider than the main view and the
   * panel at its widest — the handle (1rem) between — or, folded, the main
   * view and the strip. Anything the group had over would land in the main
   * view, after a form that does not stretch: a gap between the journal and
   * its documents. Capped like this, the spare goes outside, and the page
   * sits in the middle of it.
   *
   * `width` stays the most the page may take: below the main view and the
   * panel at its widest, the panel stops short of its maxSize instead.
   */
  const fitted = panelTakesSpare && config.mainWidth && sidePanelConfig?.maxSize
    ? (showPanel
      ? `calc(${config.mainWidth} + ${sidePanelConfig.maxSize} + 1rem)`
      : `calc(${config.mainWidth} + 48px + 1rem)`)
    : null
  const pageWidth = fitted
    ? (config.width ? `min(${config.width}, ${fitted})` : fitted)
    : config.width

  const strip = sidePanelConfig && collapsible && collapsed && (
    <FoldedStrip>
      <SideForm formType={config.viewKind ?? config.mainFormType}
        config={sidePanelConfig} validated={validated} options={options}
        collapsed onToggleCollapsed={toggleCollapsed} ></SideForm>
    </FoldedStrip>
  )

  return (
    <>
      {!isMobile && <MultiViewWrapper width={pageWidth} $wideScreen={config.wideScreen} $align={config.align} onPointerDown={handlePointerDown}>
        {sidePanelConfig?.position === "left" && strip}
        {entity &&
          <PanelGroup direction='horizontal' onLayoutChanged={handleLayoutChanged}>
            {showPanel && sidePanelConfig.position === "left" && (
              <>
                {sidePanel}
                <ResizeHandle></ResizeHandle>

              </>
            )}
            <Panel
              id="main"
              panelRef={mainPanelRef}
              // With the panel beside it taking the spare, the main view opens
              // at its width — `mainWidth`, or as the user last dragged it —
              // and never grows past `mainWidth`: wider is only empty space.
              // When the page narrows (the navigation opens, the window
              // shrinks) both sides give way by their share, rather than the
              // whole of it coming out of the document.
              {...(panelTakesSpare && showPanel && {
                defaultSize: keptWidthRef.current ? `${keptWidthRef.current}px` : config.mainWidth,
                maxSize: config.mainWidth,
                onResize: handleMainResize
              })}
            >
              <Wrapper>
                {isCard    && <CardView type={mainView} options={options} dispatch={dispatch} /> }
                {isDisplay && <DisplayView type={mainView} options={options} />}
              </Wrapper>
            </Panel>
            {showPanel && sidePanelConfig.position === "right" && (
              <>
                <ResizeHandle></ResizeHandle>
                {sidePanel}
              </>
            )}
          </PanelGroup>}
        {sidePanelConfig?.position === "right" && strip}
      </MultiViewWrapper>}
      {isMobile && isCard    && <CardView type={mainView} options={options} />}
      {isMobile && isDisplay && <DisplayView type={mainView} options={options} />}
    </>
  );
};

export default ResizableView;
